'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';

import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import { Mail, ArrowLeft, Shield, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

type ForgotForm = { email: string };

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const supabase = createClient();

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotForm>();

  const onSubmit = async (data: ForgotForm) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSentEmail(data.email);
      setSent(true);
    } catch (err: any) {
      // Security: always show success to avoid email enumeration
      setSentEmail(data.email);
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen nautex-gradient-bg flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-sm text-foreground tracking-tight">NautEX</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="card-surface shadow-2xl animate-slide-up">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Mail size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground">Reset Password</h1>
                  <p className="text-xs text-muted-foreground">We'll send a reset link to your email</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {sent ? (
                <div className="text-center space-y-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
                    <CheckCircle2 size={28} className="text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground mb-1">Check your inbox</h2>
                    <p className="text-sm text-muted-foreground">
                      If an account exists for <span className="font-medium text-foreground">{sentEmail}</span>, a password reset link has been sent.
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/20 p-3 text-xs text-muted-foreground">
                    The link expires in 1 hour. Check your spam folder if you don't see it.
                  </div>
                  <Link
                    href="/platform-user-login"
                    className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    Back to Sign In
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email"
                        placeholder="your.email@company.com"
                        className="input-base w-full pl-9 pr-3 py-2.5 text-sm"
                        {...register('email', {
                          required: 'Please enter your email address',
                          pattern: { value: /^\S+@\S+\.\S+$/, message: 'Please enter a valid email address' },
                        })}
                      />
                    </div>
                    {errors.email && (
                      <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1.5">
                        <AlertCircle size={12} />
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <><Loader2 size={16} className="animate-spin" />Sending reset link...</>
                    ) : (
                      <>Send Reset Link<Mail size={16} /></>
                    )}
                  </button>

                  <div className="text-center">
                    <Link
                      href="/platform-user-login"
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowLeft size={13} />
                      Back to Sign In
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Security note */}
          <div className="flex items-center gap-2 justify-center mt-4">
            <Shield size={13} className="text-muted-foreground" />
            <p className="text-xs text-muted-foreground">All login events are recorded in the NautEX audit trail.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
